/**
 * @engine/physics2d - Unified Physics Engine Interface
 *
 * Backend-agnostic interface that both the built-in solver and Rapier2D
 * (or any future engine) must implement. The PhysicsSubsystem talks
 * exclusively through this contract.
 *
 * @module @engine/physics2d/engine-interface
 */

// ============================================================================
// VALUE TYPES (shared across backends)
// ============================================================================

export interface PhysVec2 {
  x: number;
  y: number;
}

/** How to combine physics material properties when two colliders interact */
export const enum CombineMode {
  /** Average of the two values */
  Average = 0,
  /** Use the smaller value */
  Minimum = 1,
  /** Use the larger value */
  Maximum = 2,
  /** Multiply the two values */
  Multiply = 3,
}

/** Gravity zone type */
export const enum GravityZoneType {
  /** Replace global gravity with zone's gravity vector */
  Override = 0,
  /** Add zone's gravity to current gravity */
  Additive = 1,
  /** Attract toward zone center (planetary) */
  Point = 2,
  /** Multiply current gravity by scale factor */
  Scale = 3,
}

/** Gravity zone shape */
export const enum GravityZoneShape {
  /** Circular zone defined by inner/outer radius */
  Circle = 0,
  /** Rectangular zone (good for tunnels, corridors) */
  Box = 1,
  /** Capsule zone (stadium shape) */
  Capsule = 2,
  /** Polygon zone (arbitrary convex shape) */
  Polygon = 3,
}

/** Falloff curve for gravity zone strength */
export const enum GravityFalloff {
  /** No falloff — full strength everywhere */
  None = 0,
  /** Linear decrease from inner to outer radius */
  Linear = 1,
  /** Inverse square (realistic for point gravity) */
  InverseSquare = 2,
  /** Smooth hermite interpolation */
  Smoothstep = 3,
}

/** Descriptor to create a gravity zone */
export interface GravityZoneDescriptor {
  /** Unique identifier for this zone */
  guid: string;
  /** World position of zone center */
  position: PhysVec2;
  /** Zone rotation in radians */
  rotation: number;
  /** Zone scale */
  scale: PhysVec2;
  /** Zone shape */
  shape: GravityZoneShape;
  /** Zone type */
  type: GravityZoneType;
  /** Gravity vector (for Override/Additive) */
  gravity: PhysVec2;
  /** Strength (for Point type) or scale factor (for Scale type) */
  strength: number;
  /** Falloff curve */
  falloff: GravityFalloff;
  /** Inner radius — full strength (Circle/Capsule) */
  innerRadius: number;
  /** Outer radius — zero strength beyond (Circle/Capsule) */
  outerRadius: number;
  /** Size for Box shape, or capsule dimensions */
  size: PhysVec2;
  /** Direction for Capsule (0 = vertical, 1 = horizontal) */
  direction: number;
  /** Polygon points (local space) */
  points: PhysVec2[];
  /** Blending priority (higher = more influence) */
  priority: number;
  /** Layer mask — which bodies are affected */
  affectedLayers: number;
}

/** Descriptor for a buoyancy zone (water, lava) */
export interface BuoyancyZoneDescriptor {
  /** Unique identifier */
  guid: string;
  /** World position */
  position: PhysVec2;
  /** Rotation in radians */
  rotation: number;
  /** Scale */
  scale: PhysVec2;
  /** Fluid density (water ~1, lava ~3) */
  density: number;
  /** Linear drag coefficient */
  linearDrag: number;
  /** Angular drag coefficient */
  angularDrag: number;
  /** Flow velocity (current/stream) */
  flow: PhysVec2;
  /** Surface normal direction */
  surfaceNormal: PhysVec2;
  /** Zone width */
  width: number;
  /** Zone height */
  height: number;
  /** Blending priority */
  priority: number;
  /** Layer mask */
  affectedLayers: number;
}

/** Wind zone mode */
export const enum WindZoneMode {
  /** Constant direction wind */
  Directional = 0,
  /** Outward/inward from center */
  Radial = 1,
}

/** Wind zone shape */
export const enum WindZoneShape {
  Circle = 0,
  Box = 1,
}

/** Descriptor for a wind zone */
export interface WindZoneDescriptor {
  /** Unique identifier */
  guid: string;
  /** World position */
  position: PhysVec2;
  /** Rotation in radians */
  rotation: number;
  /** Scale */
  scale: PhysVec2;
  /** Wind mode (0=Directional, 1=Radial) */
  mode: WindZoneMode;
  /** Wind direction (for Directional mode) */
  direction: PhysVec2;
  /** Wind strength */
  strength: number;
  /** Random turbulence amount */
  turbulence: number;
  /** Turbulence frequency */
  turbulenceFrequency: number;
  /** Pulse oscillation amount */
  pulse: number;
  /** Pulse frequency (Hz) */
  pulseFrequency: number;
  /** Zone shape (0=Circle, 1=Box) */
  shape: WindZoneShape;
  /** Zone radius (Circle) */
  radius: number;
  /** Zone width (Box) */
  width: number;
  /** Zone height (Box) */
  height: number;
  /** Falloff type (0=None, 1=Linear, 2=Smoothstep) */
  falloff: number;
  /** Falloff start distance */
  falloffStart: number;
  /** Blending priority */
  priority: number;
  /** Layer mask */
  affectedLayers: number;
}

/** Descriptor for a surface effector (conveyor belt) */
export interface SurfaceEffectorDescriptor {
  /** Unique identifier */
  guid: string;
  /** Surface speed (positive = right, negative = left) */
  speed: number;
  /** Speed variation */
  speedVariation: number;
  /** Force scale */
  forceScale: number;
  /** Use contact normal for force direction */
  useContactNormal: boolean;
  /** Apply surface friction */
  useFriction: boolean;
  /** Apply surface bounce */
  useBounce: boolean;
}

/** Physics material properties for a collider */
export interface PhysicsMaterial {
  friction: number;
  bounciness: number;
  density: number;
  /** How to combine friction when two colliders touch */
  frictionCombine?: CombineMode;
  /** How to combine bounciness when two colliders touch */
  bouncinessCombine?: CombineMode;
}

/** Descriptor to create a rigid body */
export interface RigidBodyDescriptor {
  guid: string;
  gameObjectId: number;
  bodyType: PhysicsBodyType;
  position: PhysVec2;
  /** Rotation in radians */
  rotation: number;
  mass?: number;
  linearDamping?: number;
  angularDamping?: number;
  gravityScale?: number;
  /** Bitfield: bit0=freezeX, bit1=freezeY, bit2=freezeRot */
  constraints?: number;
  /** Enable CCD for fast-moving bodies */
  ccdEnabled?: boolean;
  /** Dominance priority (-127 to 127). Higher dominance wins collisions. */
  dominance?: number;
  /** Linear velocity threshold for sleeping (0 = use engine default) */
  sleepLinearThreshold?: number;
  /** Angular velocity threshold for sleeping (0 = use engine default) */
  sleepAngularThreshold?: number;
}

/** Descriptor to create a collider */
export interface ColliderDescriptor {
  shape: ColliderShape;
  offset?: PhysVec2;
  rotation?: number;
  isTrigger?: boolean;
  material?: PhysicsMaterial;
  layerMask?: number;
  collisionMask?: number;
  /** One-way platform - allows passing through from one side */
  oneWay?: boolean;
  /** Direction of one-way platform (normal pointing to solid side) */
  oneWayDirection?: PhysVec2;
}

/** Discriminated union for collider shapes */
export type ColliderShape =
  | { type: 'box'; halfExtents: PhysVec2 }
  | { type: 'circle'; radius: number }
  | { type: 'capsule'; halfHeight: number; radius: number }
  | { type: 'polygon'; vertices: PhysVec2[] };

/** Body type enum (matches component dropdown values) */
export enum PhysicsBodyType {
  Dynamic = 0,
  Kinematic = 1,
  Static = 2,
}

// ============================================================================
// READ-BACK
// ============================================================================

/** Minimal body state returned after simulation */
export interface BodyTransform {
  guid: string;
  gameObjectId: number;
  bodyType: PhysicsBodyType;
  position: PhysVec2;
  /** Rotation in radians */
  rotation: number;
}

// ============================================================================
// QUERIES
// ============================================================================

export interface RaycastHit {
  point: PhysVec2;
  normal: PhysVec2;
  distance: number;
  bodyHandle: PhysicsBodyHandle;
}

// ============================================================================
// EVENTS
// ============================================================================

export interface CollisionEventData {
  bodyHandleA: PhysicsBodyHandle;
  bodyHandleB: PhysicsBodyHandle;
  started: boolean;
}

export interface TriggerEventData {
  bodyHandleA: PhysicsBodyHandle;
  bodyHandleB: PhysicsBodyHandle;
  started: boolean;
}

/** Collision callback with contact info */
export type PhysicsCollisionCallback = (otherGuid: string, contactX: number, contactY: number, normalX: number, normalY: number) => void;
/** Trigger callback */
export type PhysicsTriggerCallback = (otherGuid: string) => void;

/** Event handlers that can be attached to a physics body */
export interface PhysicsBodyEventHandlers {
  onCollisionEnter?: PhysicsCollisionCallback;
  onCollisionStay?: PhysicsCollisionCallback;
  onCollisionExit?: PhysicsCollisionCallback;
  onTriggerEnter?: PhysicsTriggerCallback;
  onTriggerStay?: PhysicsTriggerCallback;
  onTriggerExit?: PhysicsTriggerCallback;
}

// ============================================================================
// HANDLES
// ============================================================================

/**
 * Opaque handle to a physics body inside the engine.
 * Each backend returns its own kind of handle; the subsystem
 * stores them in a GUID→handle map but never inspects internals.
 */
export type PhysicsBodyHandle = number | object;
export type PhysicsColliderHandle = number | object;

// ============================================================================
// ENGINE INTERFACE
// ============================================================================

/**
 * The contract every 2D physics backend must implement.
 *
 * Lifecycle:
 *   1. `init()` — one-time async setup (WASM loading, etc.)
 *   2. `createWorld(config)` — start a new simulation
 *   3. body/collider CRUD during scene scan
 *   4. `step(dt)` per tick
 *   5. `forEachBody(fn)` to sync back
 *   6. `destroyWorld()` when leaving play mode
 *   7. `dispose()` for full teardown
 */
export interface IPhysicsEngine2D {
  /** Human-readable name shown in the UI (e.g. "Built-in", "Rapier2D") */
  readonly name: string;

  // ──────── Lifecycle ────────

  /** Async initialization (WASM load, etc.). Called once. Optional wasmUrl for custom WASM location. */
  init(wasmUrl?: string): Promise<void>;

  /** Create a new physics world with the given gravity + config. */
  createWorld(config: PhysicsWorldOptions): void;

  /** Tear down the current world, freeing all bodies/colliders. */
  destroyWorld(): void;

  /** Full teardown (free WASM memory, etc.) */
  dispose(): void;

  // ──────── Bodies ────────

  /** Create a rigid body. Returns an opaque handle. */
  createBody(desc: RigidBodyDescriptor): PhysicsBodyHandle;

  /** Remove a rigid body and its attached colliders. */
  removeBody(handle: PhysicsBodyHandle): void;

  // ──────── Colliders ────────

  /** Attach a collider to an existing body. */
  addCollider(bodyHandle: PhysicsBodyHandle, desc: ColliderDescriptor): PhysicsColliderHandle;

  /** Remove a collider. */
  removeCollider(handle: PhysicsColliderHandle): void;

  // ──────── Simulation ────────

  /**
   * Advance the simulation by `dt` seconds.
   * The engine may use an internal accumulator / fixed timestep.
   */
  step(dt: number): void;

  // ──────── Read-back ────────

  /** Iterate all non-static bodies and call `fn` with their current transforms. */
  forEachBody(fn: (body: BodyTransform) => void): void;

  // ──────── Forces ────────

  applyForce(handle: PhysicsBodyHandle, force: PhysVec2): void;
  applyImpulse(handle: PhysicsBodyHandle, impulse: PhysVec2): void;
  applyTorque(handle: PhysicsBodyHandle, torque: number): void;
  applyAngularImpulse(handle: PhysicsBodyHandle, impulse: number): void;

  // ──────── Velocity ────────

  getLinearVelocity(handle: PhysicsBodyHandle): PhysVec2;
  setLinearVelocity(handle: PhysicsBodyHandle, velocity: PhysVec2): void;
  getAngularVelocity(handle: PhysicsBodyHandle): number;
  setAngularVelocity(handle: PhysicsBodyHandle, velocity: number): void;

  // ──────── Body Properties ────────

  setLinearDamping(handle: PhysicsBodyHandle, damping: number): void;
  setAngularDamping(handle: PhysicsBodyHandle, damping: number): void;
  setGravityScale(handle: PhysicsBodyHandle, scale: number): void;
  getMass(handle: PhysicsBodyHandle): number;
  setMass(handle: PhysicsBodyHandle, mass: number): void;

  // ──────── Sleep ────────

  sleep(handle: PhysicsBodyHandle): void;
  wake(handle: PhysicsBodyHandle): void;
  isSleeping(handle: PhysicsBodyHandle): boolean;

  // ──────── Overlap Queries ────────

  overlapCircle(center: PhysVec2, radius: number, layerMask?: number): PhysicsBodyHandle[];
  overlapBox(center: PhysVec2, halfExtents: PhysVec2, layerMask?: number): PhysicsBodyHandle[];
  overlapPoint(point: PhysVec2, layerMask?: number): PhysicsBodyHandle[];

  // ──────── Queries ────────

  raycast(origin: PhysVec2, direction: PhysVec2, maxDistance: number, layerMask?: number): RaycastHit | null;
  raycastAll(origin: PhysVec2, direction: PhysVec2, maxDistance: number, layerMask?: number): RaycastHit[];

  // ──────── Gravity Zones ────────

  /** Add a gravity zone. */
  addGravityZone(desc: GravityZoneDescriptor): void;

  /** Update gravity zone transform (when GameObject moves/rotates/scales). */
  updateGravityZonePosition(guid: string, position: PhysVec2, rotation?: number, scale?: PhysVec2): void;

  /** Update gravity zone config. */
  updateGravityZone(guid: string, desc: Partial<GravityZoneDescriptor>): void;

  /** Remove a gravity zone. */
  removeGravityZone(guid: string): void;

  // ──────── Effectors ────────

  /** Add a buoyancy zone. */
  addBuoyancyZone(desc: BuoyancyZoneDescriptor): void;

  /** Update buoyancy zone transform. */
  updateBuoyancyZonePosition(guid: string, position: PhysVec2, rotation?: number, scale?: PhysVec2): void;

  /** Update buoyancy zone config. */
  updateBuoyancyZone(guid: string, desc: Partial<BuoyancyZoneDescriptor>): void;

  /** Remove a buoyancy zone. */
  removeBuoyancyZone(guid: string): void;

  /** Add a wind zone. */
  addWindZone(desc: WindZoneDescriptor): void;

  /** Update wind zone transform. */
  updateWindZonePosition(guid: string, position: PhysVec2, rotation?: number, scale?: PhysVec2): void;

  /** Update wind zone config. */
  updateWindZone(guid: string, desc: Partial<WindZoneDescriptor>): void;

  /** Remove a wind zone. */
  removeWindZone(guid: string): void;

  /** Add a surface effector. */
  addSurfaceEffector(desc: SurfaceEffectorDescriptor): void;

  /** Update surface effector config. */
  updateSurfaceEffector(guid: string, desc: Partial<SurfaceEffectorDescriptor>): void;

  /** Remove a surface effector. */
  removeSurfaceEffector(guid: string): void;

  // ──────── Events ────────

  /** Register collision/trigger event handlers for a body. */
  setEventHandler(handle: PhysicsBodyHandle, handlers: PhysicsBodyEventHandlers): void;

  // ──────── Diagnostics ────────

  /** Get physics diagnostics for profiling. */
  getDiagnostics?(): PhysicsDiagnostics;
}

/** Options passed to `createWorld()` */
export interface PhysicsWorldOptions {
  gravity?: PhysVec2;
  fixedTimestep?: number;
  velocityIterations?: number;
  positionIterations?: number;
  allowSleeping?: boolean;
}

/** Physics engine diagnostics for profiling */
export interface PhysicsDiagnostics {
  /** Total body count */
  bodyCount: number;
  /** Total collider count */
  colliderCount: number;
  /** Number of active (non-sleeping) bodies */
  activeBodies: number;
  /** Number of sleeping bodies */
  sleepingBodies: number;
  /** Number of broad-phase pairs tested */
  broadPhasePairs: number;
  /** Number of narrow-phase contacts */
  narrowPhaseContacts: number;
  /** Number of islands */
  islandCount: number;
  /** Physics step count this frame */
  stepCount: number;
  /** Time spent in last physics step (ms) */
  stepTimeMs: number;
}
